/**
 * Vehicle trade data wizard tech subsection
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { wizardState } from '../../../redux/wizardSlice';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    width: '100%',
    marginBottom: 20,
    position: 'relative',
  },
  isInvalid: {
    '& > select': {
      borderColor: '#d50000',
    },
    '& input[type=text]': {
      borderColor: '#d50000',
    },
    '& > .mdl-textfield__error': {
      visibility: 'visible',
    },
  },
  title: {
    display: 'inline-block',
    width: '100%',
    fontSize: 18,
    lineHeight: '1.5em',
    marginBottom: 10,
    color: theme.palette.primary.main,
    '& > svg': {
      height: 18,
      width: 'auto',
      marginRight: 12,
      fill: theme.palette.primary.main,
      '& line': {
        stroke: `${theme.palette.primary.main} !important`,
      },
      '& path[stroke], path[thickness]': {
        stroke: `${theme.palette.primary.main} !important`,
      },
    },
  },
  button: {
    display: 'inline-block',
    boxSizing: 'border-box',
    paddingLeft: 12,
    paddingRight: 12,
    marginRight: 15,
    marginBottom: 10,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.type === 'dark' ? '#666666' : '#dcdcdc'} !important`,
    '&:last-of-type': {
      marginRight: 0,
    },
    '&.selected, &:hover, &:active, &:focus': {
      color: theme.palette.primary.main,
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
}));

/**
 * @param type {string}: lookup layout (buttons or combobox)
 * @param identifier {string}: subsection identifier
 * @param title {string}: subsection title
 * @param icon {node}: subsection title icon
 * @param items {array}: lookup content
 * @param value {string}: current lookup value
 * @param update {function}: quote state update callback
 * @param message {string}: error message
 * @param manageCustom {boolean}: activate field custom section
 * @param customValue {string}: field custom section value
 * @param customSelectLabel {string}: field custom section filter select label
 * @param customTextLabel {string}: field custom section filter text label
 * @param hide {boolean}: hide field
 * @returns {JSX.Element}
 */
function TechLookup({
  type, identifier, title, icon,
  items, value, update, message,
  manageCustom, customValue, customSelectLabel, customTextLabel, hide,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const { validateForm } = useSelector(wizardState);
  const isValidField = () => {
    const isValid = typeof items === 'object'
      && items.length > 0
      && typeof value === 'string'
      && value !== '';

    if (manageCustom) {
      return isValid
        && (
          (value !== '0' && value !== '')
          || (value === '0' && typeof customValue === 'string' && customValue !== '')
        );
    }

    return isValid;
  };

  const handleButtonChange = (event) => {
    const { target } = event;
    const { innerText: text, dataset } = target; // dataset contains 'data' attributes
    update(identifier, dataset.id.toString(), text);
  };

  const handleComboboxChange = (event) => {
    const { target } = event;
    const { options, selectedIndex } = target;
    const newValue = target.value.toString();

    if (!manageCustom) {
      update(identifier, newValue, options[selectedIndex].text);
    } else if (manageCustom && newValue === '0') {
      update(identifier, newValue, '', '');
    } else if (manageCustom && newValue !== '0') {
      update(identifier, newValue, options[selectedIndex].text, '');
    }
  };

  const handleCustomTextChange = (event) => {
    const { target } = event;
    const { value: newCustomValue } = target;

    update(identifier, '0', '', newCustomValue);
  };
  if (hide) {
    return <div />;
  }
  return (
    <div
      id={`ga-tech-lookup-${identifier}`}
      className={`${classes.wrapper}${!isValidField() && validateForm ? ` ${classes.isInvalid}` : ''} ga-tech-lookup`}
    >
      <span className={`${classes.title} ga-tech-lookup-title`}>
        {icon}
        {title}
      </span>
      {
        type === 'buttons'
        && items.map((item) => (
          <button
            type="button"
            className={
              `ga-button ${classes.button} ${item.id === value ? ' selected' : ''} ga-tech-lookup-button`
            }
            key={uuid()}
            data-id={item.id}
            onClick={handleButtonChange}
            disabled={item.id === value}
          >
            {item.text}
          </button>
        ))
      }
      {
        type === 'combobox'
        && (
          <>
            <select
              className="ga-select ga-tech-lookup-select"
              value={value}
              onChange={handleComboboxChange}
            >
              <option value="">{`${t('Select')} *`}</option>
              {items.map((item) => (
                <option
                  value={item.id}
                  key={uuid()}
                >
                  {item.text}
                </option>
              ))}
              {manageCustom && <option value="0" key={uuid()}>{customSelectLabel}</option>}
            </select>
            {manageCustom && value === '0'
             && (
               <div
                 className={
                   'mdl-textfield mdl-textfield--floating-label '
                   + `mdl-js-textfield${customValue !== '' ? ' is-dirty' : ''}`
                 }
                 style={{ marginTop: '15px', marginBottom: '5px' }}
               >
                 <input
                   className="mdl-textfield__input"
                   type="text"
                   value={customValue}
                   onChange={handleCustomTextChange}
                   id="customVersion"
                 />
                 <label className="mdl-textfield__label" htmlFor="customVersion">
                   {`${customTextLabel} *`}
                 </label>
               </div>
             )}
          </>
        )
      }
      {validateForm && (
        <span className="mdl-textfield__error">{t(message)}</span>
      )}
    </div>
  );
}

TechLookup.propTypes = {
  type: PropTypes.string,
  identifier: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      text: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string,
  update: PropTypes.func.isRequired,
  message: PropTypes.string,
  manageCustom: PropTypes.bool,
  customValue: PropTypes.string,
  customSelectLabel: PropTypes.string,
  customTextLabel: PropTypes.string,
  hide: PropTypes.bool,
};

TechLookup.defaultProps = {
  type: 'buttons',
  value: '',
  message: 'Fill the field.',
  manageCustom: false,
  customValue: '',
  customSelectLabel: null,
  customTextLabel: null,
  hide: false,
};

export default TechLookup;
